import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock3, RefreshCw, Star } from "lucide-react";
import OutOfStockAlternativePopup from "./OutOfStockAlternativePopup";
import ReviewPopup from './ReviewPopup';
import { viewSubsititutionDetail } from "../../redux/actions/action-creator";

const FALLBACK_PRODUCT_IMAGE = "assets/images/thumbs/product-two-img1.png";

const SECTION_META = {
  confirmed_items: {
    title: "Confirm Items",
    modifier: "confirmed",
    Icon: CheckCircle2,
  },
  pending_items: {
    title: "Pending Items",
    modifier: "pending",
    Icon: Clock3,
  },
  substitute_items: {
    title: "Substitute Items",
    modifier: "substitute",
    Icon: RefreshCw,
  },
};

const getValue = (item, keys, fallback = "") => {
  const matchedKey = keys.find((key) => item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== "");
  return matchedKey ? item[matchedKey] : fallback;
};

const formatAmount = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const numericValue = Number(value);

  if (!Number.isNaN(numericValue)) {
    return numericValue.toFixed(2);
  }

  return value;
};

const getItemTitle = (item) =>
  getValue(item, ["title", "product_name", "product_title", "name"]) ||
  getValue(item?.product, ["title", "product_name", "product_title", "name"], "Product");

const getItemImage = (item) =>
  getValue(
    item,
    ["image", "product_image", "product_cover_image", "image_url", "thumbnail"]
  ) ||
  getValue(
    item?.product,
    ["image", "product_image", "product_cover_image", "image_url", "thumbnail"],
    FALLBACK_PRODUCT_IMAGE
  );

const getItemUnit = (item) =>
  getValue(item, ["weight_unit", "unit", "portion", "product_unit", "quantity_label"]) ||
  getValue(item?.product, ["weight_unit", "unit", "portion", "product_unit", "quantity_label"]);

const getItemPrice = (item) =>
  getValue(item, ["product_price", "price", "selling_price", "final_price"]) ||
  getValue(item?.product, ["product_price", "price", "selling_price", "final_price"]);

const getOriginalPrice = (item) =>
  getValue(item, ["original_price", "mrp", "product_original_price", "actual_price"]) ||
  getValue(item?.product, ["original_price", "mrp", "product_original_price", "actual_price"]);

const getSavings = (item) =>
  getValue(item, ["savings", "saving", "discount_amount", "discount"]) ||
  getValue(item?.product, ["savings", "saving", "discount_amount", "discount"]);

const getItemRating = (item) => {
  const rating = Number(
    item?.user_rating?.rating ||
      item?.avg_rating ||
      item?.average_rating ||
      item?.rating ||
      item?.product_rating ||
      0
  );

  return Number.isFinite(rating) && rating > 0 ? rating.toFixed(1) : "";
};

const isOutOfStock = (item) => {
  const stockValue =
    item?.out_of_stock_status ??
    item?.is_out_of_stock ??
    item?.out_of_stock ??
    item?.stock_status;
  const normalizedStockValue = String(stockValue).toLowerCase().trim();

  return (
    stockValue === true ||
    stockValue === 1 ||
    stockValue === "1" ||
    normalizedStockValue === "out of stock" ||
    normalizedStockValue === "out_of_stock"
  );
};

const isTruthyStatus = (value) => {
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;

  const normalizedValue = String(value || "").toLowerCase().trim();
  return ["true", "yes", "delivered", "deliver", "completed", "complete", "replaced"].includes(normalizedValue);
};

const isItemDelivered = (item) => {
  const deliveredValue =
    item?.is_delivered ??
    item?.delivered ??
    item?.item_delivered ??
    item?.delivery_status ??
    item?.item_status ??
    item?.status_label ??
    item?.status;

  return isTruthyStatus(deliveredValue);
};

const isItemReplaced = (item, delivered = false) => {
  const replacedValue =
    item?.is_replaced ??
    item?.replaced ??
    item?.item_replaced ??
    item?.replacement_status ??
    item?.substitute_status ??
    item?.substitution_status;

  return isTruthyStatus(replacedValue) || delivered;
};

const renderItemUnit = (unit) => {
  const unitText = String(unit || "").trim();

  if (!unitText) return null;

  const [label, ...rest] = unitText.split(":");

  if (!rest.length) {
    return <span className="order-product-unit-label">{unitText}</span>;
  }

  return (
    <>
      <span className="order-product-unit-label">{label.trim()}</span>
      <span className="order-product-unit-value">{rest.join(":").trim()}</span>
    </>
  );
};

const Rating = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const halfStar = rating % 1 >= 0.5;
  const totalStars = 5;

  return (
    <div className="rating">
      {[...Array(totalStars)].map((_, index) => {
        if (index < fullStars) {
          return <span key={index} className="filled">★</span>;
        } else if (index === fullStars && halfStar) {
          return <span key={index} className="half">★</span>;
        } else {
          return <span key={index}>★</span>;
        }
      })}
    </div>
  );
};

const OrderDetails = ({ordersDetail}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showReview, setShowReview] = useState(false);
  const [show, setShow] = useState(false);
  const [alternativeDetail, setAlternativeDetail] = useState(null);
  const [alternativeLoading, setAlternativeLoading] = useState(false);
  const [alternativeError, setAlternativeError] = useState("");
  const [items, setItems] = useState({
    confirmed_items: [],
    pending_items: [],
    substitute_items: [],
  });

  // State mein add karein (existing showReview ke saath)
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const productData = ordersDetail?.product_data || {};
    const getItemsByKey = (key) => {
      if (Array.isArray(productData?.[key])) {
        return productData[key];
      }
      if (Array.isArray(ordersDetail?.[key])) {
        return ordersDetail[key];
      }
      return [];
    };

    setItems({
      confirmed_items: getItemsByKey("confirmed_items"),
      pending_items: getItemsByKey("pending_items"),
      substitute_items: getItemsByKey("substitute_items"),
    });
  }, [ordersDetail]);

  const itemSections = Object.keys(SECTION_META)
    .map((key) => ({
      key,
      ...SECTION_META[key],
      data: items[key],
    }))
    .filter((section) => section.data.length > 0);
  const showAlternativeButton =
    ordersDetail?.substitution !== undefined &&
    ordersDetail?.substitution !== null &&
    Number(ordersDetail.substitution) !== 0;

  const isSuccessResponse = (response) =>
    response?.result === true || response?.result === 1 || response?.result === "1";

  const getMerchantId = () =>
    ordersDetail?.merchant_id ||
    ordersDetail?.merchantId ||
    1;

  // Handler function
  const handleWriteReview = (item) => {
    setSelectedProduct(item);
    setShowReview(true);
  };

  const handleReviewSubmit = (data) => {
    setItems((prevItems) => {
      const updatedItems = {};

      Object.entries(prevItems).forEach(([sectionKey, sectionItems]) => {
        updatedItems[sectionKey] = sectionItems.map((item) => {
          if ((item.product_variant_id || item.variant_id) === data.product_variant_id) {
            return {
              ...item,
              user_rating: {
                rating: data.rating
              }
            };
          }
          return item;
        });
      });

      return updatedItems;
    });
  };

  const handleViewAlternative = async () => {
    const orderId = ordersDetail?.order_id;

    if (!orderId || alternativeLoading) return;

    setShow(true);
    setAlternativeLoading(true);
    setAlternativeError("");
    setAlternativeDetail(null);

    try {
      const response = await dispatch(
        viewSubsititutionDetail({
          order_id: orderId,
          merchant_id: getMerchantId(),
        })
      );

      if (isSuccessResponse(response)) {
        setAlternativeDetail(response);
      } else {
        setAlternativeError(response?.message || "Unable to load alternative items.");
      }
    } catch (error) {
      setAlternativeError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load alternative items."
      );
    } finally {
      setAlternativeLoading(false);
    }
  };

  const handleAlternativeClose = () => {
    setShow(false);
    setAlternativeError("");
  };

  const goToOrderTracking = () => {
    const orderId = ordersDetail?.order_id;
    const merchantId = ordersDetail?.merchant_id || ordersDetail?.merchantId || 1;

    if (!orderId) return;

    navigate(`/order-tracking?order_id=${orderId}&merchant_id=${merchantId}`, {
      state: {
        order_id: orderId,
        merchant_id: merchantId,
      },
    });
  };

  return (
    <>
    <div className="card order-detail-card">

      {/* HEADER */}
      <div className="card-body border-bottom order-detail-header">
        <div className="order-detail-topline">
          <div className="order-detail-store">
            <img
              src={ordersDetail?.business_logo}
              className="rounded order-detail-store-image"
              alt={ordersDetail?.business_segment_name}
            />
            <div className="order-detail-store-info">
              <div className="order-detail-store-title-row">
                <h6 className="order-detail-store-name">{ordersDetail?.business_segment_name}</h6>
                <div className="order-detail-header-actions">
                  <button className="submit-btn order-detail-header-chip order-detail-payment-chip">
                    {ordersDetail?.payment_method}
                  </button>
                  <button className="btn bg-main-three-800 btn-sm order-detail-header-chip order-detail-status-chip">
                    {ordersDetail?.order_status}
                  </button>
                </div>
              </div>
              <small className="order-detail-store-address">
                {ordersDetail?.pickup}
              </small>
              <div className="order-detail-store-price">
                {ordersDetail?.currency} {ordersDetail?.receipt?.total_amount}
              </div>
              <div className="order-detail-inline-meta">
                <span>{ordersDetail?.deliver_label}: {ordersDetail?.deliver_on}</span>
                <span>{ordersDetail?.order_date_label}: {ordersDetail?.order_date}</span>
                <span>Total Items: {ordersDetail?.total_items}</span>
              </div>
              {ordersDetail?.order_status !== "Order Delivered" && (
                <div className="order-detail-tracking-row">
                  <button
                    onClick={goToOrderTracking}
                    className="order-detail-tracking-btn"
                  >
                    <i className="ph ph-map-pin" aria-hidden="true" />
                    <span>Order Tracking</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {showAlternativeButton && (
            <div
              className="order-detail-status-panel"
            >
              <div className="order-detail-action-group">
                <button
                  className="submit-btn"
                  onClick={handleViewAlternative}
                  disabled={alternativeLoading}
                >
                  {alternativeLoading ? "Loading..." : "View Alternative"}
                </button>
              </div>
            </div>
          )}


        </div>
      </div>

      {/* ITEMS */}
      <div className="card-body order-items-body">
        {itemSections.length === 0 && (
          <div className="order-products-empty">No items found for this order.</div>
        )}

        {itemSections.map((section) => {
          const SectionIcon = section.Icon;

          return (
            <div key={section.key} className={`mb-4 order-item-section order-item-section--${section.modifier}`}>
              <div className="order-item-section-title">
                <span className="order-item-section-icon" aria-hidden="true">
                  <SectionIcon size={13} strokeWidth={2.4} />
                </span>
                <h6 className="fw-semibold mb-0">{section.title}</h6>
                <span className="order-item-section-count">{section.data.length} Items</span>
              </div>

              <div className="order-item-section-list">
              {section.data.map((item, i) => {
                const title = getItemTitle(item);
                const image = getItemImage(item);
                const unit = getItemUnit(item);
                const price = getItemPrice(item);
                const originalPrice = getOriginalPrice(item);
                const savings = getSavings(item);
                const rating = getItemRating(item);
                const hasVariant = item?.product_variant_id || item?.variant_id;
                const isSubstituteItem = section.key === "substitute_items";
                const itemOutOfStock = isSubstituteItem && isOutOfStock(item);
                const itemDelivered = isSubstituteItem && !itemOutOfStock && isItemDelivered(item);
                const itemReplaced = isSubstituteItem && !itemOutOfStock && isItemReplaced(item, itemDelivered);
                const rowStatusClassName = [
                  `order-product-row--${section.modifier}`,
                  itemOutOfStock ? "order-product-row--out-of-stock" : "",
                  itemDelivered ? "order-product-row--delivered" : "",
                ].filter(Boolean).join(" ");

                return (
                  <div key={`${section.key}-${i}`} className={`item-row order-product-row ${rowStatusClassName}`}>

                    <div className="item-img order-product-image-wrap">
                      <img
                        src={image}
                        alt={title}
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = FALLBACK_PRODUCT_IMAGE;
                        }}
                      />
                      <span className="order-product-heart-icon" aria-hidden="true">
                        <i className="ph ph-heart" />
                      </span>
                      {rating && (
                        <span className="order-product-rating-badge">
                          <Star size={10} fill="currentColor" strokeWidth={2.4} />
                          {rating}
                        </span>
                      )}
                      {!isSubstituteItem && isOutOfStock(item) && (
                        <span className="stock-badge">Out of Stock</span>
                      )}
                    </div>

                    <div className="item-info order-product-info">
                      <div className="fw-semibold order-product-title">{title}</div>
                      {unit && (
                        <small className="order-product-unit">
                          {renderItemUnit(unit)}
                        </small>
                      )}
                      {price !== "" && (
                        <div className="fw-bold order-product-price">
                          {ordersDetail?.currency} {formatAmount(price)}
                        </div>
                      )}
                      <small className="text-muted order-product-meta">
                        {originalPrice !== "" && (
                          <span>Original Price: {formatAmount(originalPrice)}</span>
                        )}
                        {savings !== "" && (
                          <span>Savings: {formatAmount(savings)}</span>
                        )}
                      </small>
                    </div>

                    {(itemOutOfStock || itemDelivered || itemReplaced) && (
                      <div className="order-product-status-stack" aria-label="Item status">
                        {itemOutOfStock && (
                          <span className="order-product-status-badge order-product-status-badge--out">
                            Item Out of Stock
                          </span>
                        )}
                        {itemDelivered && (
                          <span className="order-product-status-badge order-product-status-badge--delivered">
                            <i className="ph ph-check" aria-hidden="true" />
                            Delivered
                          </span>
                        )}
                        {itemReplaced && (
                          <span className="order-product-status-badge order-product-status-badge--replaced">
                            Item Replaced
                          </span>
                        )}
                      </div>
                    )}

                    {hasVariant && (
                      <button type="button" className="item-review order-product-review" onClick={() => handleWriteReview(item)}>
                        <Rating
                          rating={
                            Number(item?.user_rating?.rating) ||
                            parseFloat(item?.avg_rating)
                          }
                        />
                        <div className="review-link">
                          Write a Review
                        </div>
                      </button>
                    )}

                  </div>
                );
              })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    <ReviewPopup
      show={showReview}
      onClose={() => { setShowReview(false); setSelectedProduct(null); }}
      onSubmitSuccess={handleReviewSubmit}   // ✅ NEW PROP
      order_id={ordersDetail?.order_id}
      order_no={ordersDetail?.order_no}
      user_rating={Number(selectedProduct?.user_rating?.rating) || 0}
      product_variant_id={selectedProduct?.product_variant_id || selectedProduct?.variant_id}
      productName={selectedProduct ? getItemTitle(selectedProduct) : ""}
      productImage={selectedProduct ? getItemImage(selectedProduct) : ""}
      productPrice={selectedProduct ? getItemPrice(selectedProduct) : ""}
      productUnit={selectedProduct ? getItemUnit(selectedProduct) : ""}
    />
     
    <OutOfStockAlternativePopup
      show={show}
      onClose={handleAlternativeClose}
      data={alternativeDetail}
      ordersDetail={ordersDetail}
      orderId={ordersDetail?.order_id}
      merchantId={getMerchantId()}
      loading={alternativeLoading}
      error={alternativeError}
    />
    </>  
  );
};

export default OrderDetails;
